<template>
    <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-shadow-smx tw-relative tw-p-5 tw-flex tw-flex-col tw-items-center tw-justify-between tw-min-h-[280px] tw-h-full hover:tw-shadow-md hover:tw-border-primary-300 tw-transition-all tw-duration-200 tw-cursor-pointer">
        <!-- Bookmark Icon -->
        <button
            class="tw-absolute tw-top-3 tw-right-3 tw-z-10 tw-p-1.5 tw-rounded-full hover:tw-bg-gray-100 tw-transition-colors tw-duration-150"
            @click.stop="onBookmark"
            :disabled="bookmarkLoading"
            aria-label="Bookmark"
        >
            <icon
                v-if="!bookmarkLoading"
                :icon="bookmarked ? 'solar:bookmark-bold' : 'solar:bookmark-outline'"
                class="tw-text-2xl tw-text-primary-500"
            />
            <svg v-else class="tw-animate-spin tw-h-6 tw-w-6 tw-text-primary-400" fill="none" viewBox="0 0 24 24">
                <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
        </button>

        <!-- Clickable Card Content -->
        <div 
            class="tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-between tw-text-decoration-none tw-cursor-pointer"
            @click="viewService"
        >
            <!-- Service Image/Icon -->
            <div class="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-w-full tw-mb-3">
                <div v-if="service.image" class="tw-w-full tw-flex tw-justify-center">
                    <img 
                        :src="service.image" 
                        :alt="service.name || service.title" 
                        class="tw-max-h-32 tw-object-contain tw-rounded-md tw-bg-gray-50 tw-w-auto tw-h-auto" 
                    />
                </div>
                <div v-else class="tw-w-32 tw-h-32 tw-bg-gray-100 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                    <icon icon="solar:settings-bold-duotone" class="tw-text-4xl tw-text-primary-300" />
                </div>
            </div>

            <!-- Service Info -->
            <div class="tw-w-full tw-flex tw-flex-col tw-items-start tw-space-y-2">
                <!-- Service Title -->
                <div class="tw-text-md tw-font-bold tw-text-gray-900 tw-leading-tight tw-line-clamp-2" :title="service.name || service.title">
                    {{ service.name || service.title }}
                </div>

                <!-- Service Description -->
                <div v-if="service.description" class="tw-text-sm tw-text-gray-600 tw-line-clamp-2 tw-leading-relaxed">
                    {{ service.description }}
                </div>

                <!-- Categories -->
                <div v-if="service.categories && service.categories.length > 0" class="tw-flex tw-gap-1 tw-overflow-hidden tw-w-full">
                    <div class="tw-flex tw-gap-1 tw-overflow-hidden tw-flex-1">
                        <span 
                            v-for="category in service.categories.slice(0, 2)" 
                            :key="category.id"
                            class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-emerald-100 tw-text-emerald-800 tw-min-w-0 tw-flex-shrink tw-overflow-hidden"
                            :title="category.name"
                        >
                            <span class="tw-truncate">{{ category.name }}</span>
                        </span>
                    </div>
                    <span 
                        v-if="service.categories.length > 2" 
                        class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800 tw-flex-shrink-0"
                        :title="`${service.categories.length - 2} more categories`"
                    >
                        +{{ service.categories.length - 2 }}
                    </span>
                </div>

                <!-- Company Info -->
                <div v-if="service.company" class="tw-text-xs tw-text-gray-500 tw-truncate tw-w-full">
                    {{ $t('common.by', 'By') }} {{ service.company.name }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, ref } from 'vue';
import bookmarksApi from '@/api/bookmarks';

const props = defineProps({
    service: {
        type: Object,
        required: true
    }
});

const bookmarked = ref(props.service.is_bookmarked);
const bookmarkLoading = ref(false);

const onBookmark = async () => {
    if (bookmarkLoading.value) return;
    bookmarkLoading.value = true;

    await bookmarksApi.toggle({
        bookmarkable_type: 'service',
        bookmarkable_id: props.service.id
    })

    bookmarked.value = !bookmarked.value;
    bookmarkLoading.value = false;
}

// Handle service click - for now just show an alert
// TODO: Create a shared service detail route or modal
function viewService() {
    console.log('Service clicked:', props.service);
    // Placeholder - could open a modal or navigate to detail page when route is created
    alert(`Service: ${props.service.name}\nDescription: ${props.service.description || 'No description'}`);
}
</script>

<style scoped>
.tw-line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style> 